/**
 * Annotation edit windows for the case-time event log (ENG-202).
 *
 * A window is the span `annotationStartTsAtom` is set: from the first stroke /
 * Edit / prediction edit until save, cancel or reset. The atom has many
 * writers (the panel and the viewports' first-stroke paths), so instead of
 * stamping each one this hook watches the atom's transitions:
 *
 * - null → ts: `annotation_start` with a new annotation_session_id, at `ts`
 *   (the same moment as `timing.start_ts`), plus edited_annotation_id when
 *   editing a saved annotation;
 * - ts → null without a successful save: `annotation_cancel`;
 * - ts1 → ts2: cancel the old window, start a new one;
 * - unmount with a window open (case change): `annotation_cancel`.
 *
 * The save sends `saveStamp()` as `time_event`, so the backend writes
 * `annotation_save` for the same session; a retry reuses the same stamp.
 * No-op without a CaseEventLog (outside the segmentation study route).
 */
import { useCallback, useEffect, useRef, type RefObject } from "react";
import type { TimeEventStamp } from "../utils/caseEventQueue";
import { useCaseEventLogContext, type CaseEventLog } from "./useCaseEventLog";

interface AnnotationWindow {
  sessionId: string;
  /** Study the window was opened on (the prop may already show the next case). */
  studyUid: string;
  startTs: string;
  editedAnnotationId?: string;
  saved: boolean;
  stamp: TimeEventStamp | null;
}

export interface AnnotationSessionEvents {
  /** `time_event` for this window's save; the same object on every retry. */
  saveStamp: () => TimeEventStamp | null;
  /** The save succeeded: the window's end is the backend's annotation_save. */
  markSaved: () => void;
}

/** Same rule as the save payload: local placeholder ids aren't annotations yet. */
function savedAnnotationId(id: string | null | undefined): string | undefined {
  return id && !id.startsWith("local_") ? id : undefined;
}

export function useAnnotationSessionEvents(
  annotationStartTs: string | null,
  editingAnnotationIdRef: RefObject<string | null>,
  studyUid: string,
): AnnotationSessionEvents {
  const log = useCaseEventLogContext();
  const logRef = useRef<CaseEventLog | null>(log);
  logRef.current = log;
  const studyRef = useRef(studyUid);
  studyRef.current = studyUid;
  const windowRef = useRef<AnnotationWindow | null>(null);

  const closeWindow = useCallback(() => {
    const current = windowRef.current;
    windowRef.current = null;
    if (!current || current.saved) return;
    logRef.current?.add("annotation_cancel", current.studyUid, {
      annotation_session_id: current.sessionId,
      ...(current.editedAnnotationId ? { edited_annotation_id: current.editedAnnotationId } : {}),
    });
  }, []);

  useEffect(() => {
    if (windowRef.current?.startTs === annotationStartTs) return;
    closeWindow();
    const activeLog = logRef.current;
    if (!annotationStartTs || !activeLog) return;
    const opened: AnnotationWindow = {
      sessionId: crypto.randomUUID(),
      studyUid: studyRef.current,
      startTs: annotationStartTs,
      editedAnnotationId: savedAnnotationId(editingAnnotationIdRef.current),
      saved: false,
      stamp: null,
    };
    windowRef.current = opened;
    activeLog.add("annotation_start", opened.studyUid, {
      annotation_session_id: opened.sessionId,
      at: annotationStartTs,
      ...(opened.editedAnnotationId ? { edited_annotation_id: opened.editedAnnotationId } : {}),
    });
  }, [annotationStartTs, editingAnnotationIdRef, closeWindow]);

  // Case change remounts the panel before the atom's reset is observed here.
  useEffect(() => closeWindow, [closeWindow]);

  const saveStamp = useCallback((): TimeEventStamp | null => {
    const current = windowRef.current;
    const activeLog = logRef.current;
    if (!current || !activeLog) return null;
    current.stamp ??= activeLog.stamp("annotation_save", current.studyUid, {
      annotation_session_id: current.sessionId,
    });
    return current.stamp;
  }, []);

  const markSaved = useCallback(() => {
    if (windowRef.current) windowRef.current.saved = true;
  }, []);

  return { saveStamp, markSaved };
}
